import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  ScrollArea,
  TextInput,
  Select,
  Button,
  Group,
  ActionIcon,
  Paper,
  Text,
  Tooltip,
  NumberInput,
  Box,
  Badge,
  Menu,
  Checkbox,
  Loader,
  Alert,
  Divider,
} from "@mantine/core";
import {
  IconPlus,
  IconMinus,
  IconAlertCircle,
  IconCheck,
  IconTrash,
  IconDownload,
  IconUpload,
  IconCopy,
  IconFilter,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconRefresh,
  IconSettings,
} from "@tabler/icons-react";
import { filterInputByType, handleKeyDown } from "./utilsFunctions";
import { BenchTable } from "../pages/UI";

// Enhanced Types
export interface SpreadsheetCell {
  value: string;
  isValid?: boolean;
  error?: string;
  type?: "text" | "number" | "email" | "select" | "date" | "url" | "phone";
  metadata?: Record<string, any>;
}

export interface ColumnConfig {
  key: string;
  label?: string;
  required: boolean;
  type?: "text" | "number" | "email" | "select" | "date" | "url" | "phone";
  validator?: (value: string, row?: SpreadsheetCell[]) => boolean | string; // Can return error message
  options?: string[] | { value: string; label: string }[]; // Enhanced options
  placeholder?: string;
  width?: number;
  sortable?: boolean;
  filterable?: boolean;
  hidden?: boolean;
  readonly?: boolean;
  min?: number;
  max?: number;
  step?: number;
  format?: (value: string) => string;
}

export interface SpreadsheetProps {
  columns: ColumnConfig[];
  data: SpreadsheetCell[][];
  onChange: (data: SpreadsheetCell[][]) => void;
  onValidationChange?: (stats: ValidationStats) => void;
  minRows?: number;
  maxRows?: number;
  allowAddRows?: boolean;
  allowDeleteRows?: boolean;
  allowBulkOperations?: boolean;
  highlightErrors?: boolean;
  showValidation?: boolean;
  enableSearch?: boolean;
  enableSort?: boolean;
  enableFilter?: boolean;
  enableExport?: boolean;
  enableImport?: boolean;
  loading?: boolean;
  className?: string;
  title?: string;
  description?: string;
  virtualScrolling?: boolean;
  autoSave?: boolean;
  autoSaveDelay?: number;
}

export interface ValidationError {
  row: number;
  column: string; // key
  message: string;
}

export interface ValidationStats {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  requiredFieldsEmpty: number;
  errorsByColumn: Record<string, number>;
  errors: ValidationError[];
}

interface SortConfig {
  columnIndex: number;
  direction: "asc" | "desc";
}

interface FilterConfig {
  columnIndex: number;
  value: string;
  operator: "contains" | "equals" | "startsWith" | "endsWith";
}

// Enhanced validation with better error messages
const validateCell = (
  value: string,
  column: ColumnConfig,
  rowData?: SpreadsheetCell[]
): { isValid: boolean; error?: string } => {
  const trimmedValue = value.trim();

  // Check if required field is empty
  if (column.required && !trimmedValue) {
    return {
      isValid: false,
      error: `${column.label || column.key} is required`,
    };
  }

  // Skip validation for empty optional fields
  if (!trimmedValue && !column.required) {
    return { isValid: true };
  }

  // Type-specific validation with better patterns
  switch (column.type) {
    case "email":
      if (
        !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
          trimmedValue
        )
      ) {
        return { isValid: false, error: "Please enter a valid email address" };
      }
      break;
    case "number":
      const num = parseFloat(trimmedValue);
      if (isNaN(num)) {
        return { isValid: false, error: "Please enter a valid number" };
      }
      if (column.min !== undefined && num < column.min) {
        return {
          isValid: false,
          error: `Value must be at least ${column.min}`,
        };
      }
      if (column.max !== undefined && num > column.max) {
        return { isValid: false, error: `Value must not exceed ${column.max}` };
      }
      break;
    case "date":
      const date = new Date(trimmedValue);
      if (isNaN(date.getTime())) {
        // Fallback check for DD-MM-YYYY format which native Date constructor often fails to parse
        const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
        if (ddmmyyyy.test(trimmedValue)) {
          const [d, m, y] = trimmedValue.split("-").map(Number);
          const testDate = new Date(y, m - 1, d);
          if (testDate.getDate() !== d || testDate.getMonth() !== Number(m) - 1) {
            return { isValid: false, error: "Please enter a valid date" };
          }
        } else {
          return { isValid: false, error: "Please enter a valid date" };
        }
      }
      break;
    case "url":
      try {
        new URL(trimmedValue);
      } catch {
        return { isValid: false, error: "Please enter a valid URL" };
      }
      break;
    case "phone":
      if (
        !/^[\+]?[1-9][\d]{0,15}$/.test(trimmedValue.replace(/[\s\-\(\)]/g, ""))
      ) {
        return { isValid: false, error: "Please enter a valid phone number" };
      }
      break;
  }

  // Custom validator with enhanced error handling
  if (column.validator) {
    const result = column.validator(trimmedValue, rowData);
    if (result === false) {
      return {
        isValid: false,
        error: `Invalid ${column.label || column.key} format`,
      };
    }
    if (typeof result === "string") {
      return { isValid: false, error: result };
    }
  }

  return { isValid: true };
};

// Utility functions
const exportToCSV = (data: SpreadsheetCell[][], columns: ColumnConfig[]) => {
  const headers = columns
    .filter((col) => !col.hidden)
    .map((col) => col.label || col.key);
  const rows = data
    .filter((row) => row.some((cell) => cell?.value?.trim()))
    .map((row) =>
      columns
        .filter((col) => !col.hidden)
        .map((col, index) => row[index]?.value || "")
    );

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "spreadsheet-data.csv";
  link.click();
  URL.revokeObjectURL(url);
};

const EnhancedSpreadsheet: React.FC<SpreadsheetProps> = ({
  columns,
  data,
  onChange,
  onValidationChange,
  minRows = 5,
  maxRows = 1000,
  allowAddRows = true,
  allowDeleteRows = true,
  allowBulkOperations = true,
  highlightErrors = true,
  showValidation = true,
  enableSearch = true,
  enableSort = true,
  enableFilter = false,
  enableExport = true,
  enableImport = false,
  loading = false,
  className,
  title,
  description,
  virtualScrolling = false,
  autoSave = false,
  autoSaveDelay = 2000,
}) => {
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  //   const [showSettings, setShowSettings] = useState(false);
  const autoSaveTimer = useRef<NodeJS.Timeout>();
  //   console.log(data, "data");

  // Enhanced validation stats with column-wise error tracking
  const validationStats = useMemo((): ValidationStats => {
    const stats: ValidationStats = {
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      requiredFieldsEmpty: 0,
      errorsByColumn: {},
      errors: [],
    };

    // Initialize error counts for each column
    columns.forEach((col) => {
      stats.errorsByColumn[col.key] = 0;
    });

    const nonEmptyRows = data.filter((row) =>
      row.some((cell) => cell?.value && cell.value.trim() !== "")
    );

    stats.totalRows = nonEmptyRows.length;

    nonEmptyRows.forEach((row) => {
      let rowValid = true;
      let hasRequiredEmpty = false;

      row.forEach((cell, colIndex) => {
        if (colIndex < columns.length) {
          // Use existing validation state if available, otherwise validate
          const isCellValid = cell.isValid !== undefined
            ? cell.isValid
            : validateCell(cell?.value || "", columns[colIndex], row).isValid;

          if (!isCellValid) {
            rowValid = false;
            stats.errorsByColumn[columns[colIndex].key]++;
            if (columns[colIndex].required && !cell?.value?.trim()) {
              hasRequiredEmpty = true;
            }
            if (cell.error) {
              stats.errors.push({
                row: data.indexOf(row) + 1,
                column: columns[colIndex].label || columns[colIndex].key,
                message: cell.error
              });
            }
          }
        }
      });

      if (hasRequiredEmpty) {
        stats.requiredFieldsEmpty++;
      }

      if (rowValid) {
        stats.validRows++;
      } else {
        stats.invalidRows++;
      }
    });

    return stats;
  }, [data, columns]);

  // Notify parent of validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validationStats);
    }
  }, [validationStats, onValidationChange]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSave) {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
      autoSaveTimer.current = setTimeout(() => {
        // Trigger save logic here
        console.log("Auto-saving data...");
      }, autoSaveDelay);
    }

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [data, autoSave, autoSaveDelay]);

  // Enhanced cell update with batch operations
  const updateCell = useCallback(
    (rowIndex: number, colIndex: number, value: string) => {
      const newData = [...data];

      // Ensure row exists
      if (!newData[rowIndex]) {
        newData[rowIndex] = Array(columns.length)
          .fill(null)
          .map(() => ({ value: "" }));
      }

      // Validate the cell using row context
      const validation = validateCell(value, columns[colIndex], newData[rowIndex]);

      newData[rowIndex][colIndex] = {
        value,
        isValid: validation.isValid,
        error: validation.error,
        type: columns[colIndex].type || "text",
      };

      onChange(newData);
    },
    [data, columns, onChange]
  );

  // Row operations
  const addRow = useCallback(() => {
    if (data.length >= maxRows) return;

    const newRow = Array(columns.length)
      .fill(null)
      .map(() => ({
        value: "",
        isValid: true,
        type: "text" as const,
      }));

    onChange([...data, newRow]);
  }, [data, columns.length, maxRows, onChange]);

  const addMultipleRows = useCallback(
    (count: number) => {
      const newRows = Array(count)
        .fill(null)
        .map(() =>
          Array(columns.length)
            .fill(null)
            .map(() => ({
              value: "",
              isValid: true,
              type: "text" as const,
            }))
        );

      onChange([...data, ...newRows]);
    },
    [data, columns.length, onChange]
  );

  const deleteRow = useCallback(
    (rowIndex: number) => {
      //   if (data.length <= minRows) return;
      //   console.log(rowIndex, "rowIndex");

      const newData = data.filter((_, index) => index !== rowIndex);
      onChange(newData);
      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
    },
    [data, onChange]
  );

  const deleteSelectedRows = useCallback(() => {
    if (selectedRows.size === 0) return;

    const newData = data.filter((_, index) => !selectedRows.has(index));
    onChange(newData);
    setSelectedRows(new Set());
  }, [data, selectedRows, onChange]);

  // Sorting functionality
  const handleSort = useCallback(
    (columnIndex: number) => {
      if (!enableSort || !columns[columnIndex].sortable) return;

      setSortConfig((prev) => ({
        columnIndex,
        direction:
          prev?.columnIndex === columnIndex && prev.direction === "asc"
            ? "desc"
            : "asc",
      }));
    },
    [enableSort, columns]
  );

  // Filtered and sorted data
  const processedData = useMemo(() => {
    let filtered = [...data];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((row) =>
        row.some((cell) =>
          cell?.value?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply column filters
    filters.forEach((filter) => {
      filtered = filtered.filter((row) => {
        const cellValue = row[filter.columnIndex]?.value?.toLowerCase() || "";
        const filterValue = filter.value.toLowerCase();

        switch (filter.operator) {
          case "equals":
            return cellValue === filterValue;
          case "startsWith":
            return cellValue.startsWith(filterValue);
          case "endsWith":
            return cellValue.endsWith(filterValue);
          case "contains":
          default:
            return cellValue.includes(filterValue);
        }
      });
    });

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.columnIndex]?.value || "";
        const bValue = b[sortConfig.columnIndex]?.value || "";

        const comparison = aValue.localeCompare(bValue, undefined, {
          numeric: true,
        });
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    return filtered;
  }, [data, searchTerm, filters, sortConfig]);


  const renderCell = useCallback(
    (cell: SpreadsheetCell, rowIndex: number, colIndex: number) => {
      const column = columns[colIndex];

      if (column.hidden) return null;

      const statusColumnIndex = columns.findIndex(c => c.key === "Status");
      const packageColIndex = columns.findIndex(c => c.key === "Package Ref.ID");
      const subscriptionColIndex = columns.findIndex(c => c.key === "Subscription Start Date");

      const rowStatus = data[rowIndex]?.[statusColumnIndex]?.value?.trim();
      const isInactive = rowStatus === "Inactive";

      // 🔥 NEW LOGIC: For inactive row, hide only the two target cells
      if (
        isInactive &&
        (colIndex === packageColIndex || colIndex === subscriptionColIndex)
      ) {
        return (
          <Text size="xs" color="dimmed" style={{ padding: "6px 4px" }}>
            (Inactive)
          </Text>
        );
      }

      const isRequired = column.required;
      const shouldHighlight = highlightErrors && !cell?.isValid;
      const isReadonly = column.readonly;
      const isCellDisabled = cell?.metadata?.disabled === true;

      // Per-cell disabled: show N/A placeholder
      if (isCellDisabled) {
        return (
          <Text size="xs" color="dimmed" style={{ padding: "6px 8px", fontStyle: "italic", backgroundColor: "#f8f9fa", borderRadius: 4 }}>
            N/A
          </Text>
        );
      }

      const commonProps = {
        value: cell?.value || "",
        disabled: isReadonly,
        error: highlightErrors && !cell?.isValid ? cell?.error : undefined,
        size: "xs" as const,
        placeholder:
          column.placeholder || (isRequired ? "Required" : "Optional"),
        style: {
          borderColor: shouldHighlight ? "#ff6b6b" : undefined,
        },
      };

      const handleInputChange = (newValue: string) => {
        const filteredValue = filterInputByType(
          newValue,
          column.type || "text",
          column.key
        );
        updateCell(rowIndex, colIndex, filteredValue);
      };

      if (isReadonly) {
        return (
          <Text size="xs" style={{ padding: "4px 8px" }}>
            {column.format
              ? column.format(cell?.value || "")
              : cell?.value || ""}
          </Text>
        );
      }

      switch (column.type) {
        case "select":
          const options =
            column.options?.map((opt) =>
              typeof opt === "string" ? { value: opt, label: opt } : opt
            ) || [];

          return (
            <Select
              {...commonProps}
              data={options}
              searchable
              clearable
              onChange={(value) => updateCell(rowIndex, colIndex, value || "")}
            />
          );

        case "number":
          return (
            <NumberInput
              {...commonProps}
              value={cell?.value ? parseFloat(cell.value) || "" : ""}
              onChange={(value) =>
                updateCell(
                  rowIndex,
                  colIndex,
                  value !== "" ? String(value) : ""
                )
              }
              onKeyDown={(event) => handleKeyDown(event, "number", column.key)}
              precision={2}
              min={column.min}
              max={column.max}
              step={column.step}
            />
          );

        case "email":
          return (
            <TextInput
              {...commonProps}
              type="email"
              onChange={(event) => handleInputChange(event.target.value)}
              onKeyDown={(event) =>
                handleKeyDown(event, "email", column.key)
              }
            />
          );

        case "date":
          const convertToInputFormat = (dateStr: string): string => {
            if (!dateStr) return "";

            const ddMmYyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
            const ddMmYyyyMatch = dateStr.match(ddMmYyyyPattern);
            if (ddMmYyyyMatch) {
              const [, day, month, year] = ddMmYyyyMatch;
              return `${year}-${month}-${day}`;
            }

            const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
            const slashMatch = dateStr.match(slashPattern);
            if (slashMatch) {
              let [, month, day, year] = slashMatch;
              month = month.padStart(2, "0");
              day = day.padStart(2, "0");

              if (year.length === 2) {
                const yr = parseInt(year, 10);
                year = yr >= 90 ? `19${year}` : `20${year}`;
              }

              return `${year}-${month}-${day}`;
            }

            return "";
          };

          const convertToStorageFormat = (dateStr: string): string => {
            if (!dateStr) return "";
            const yyyyMmDdPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
            const match = dateStr.match(yyyyMmDdPattern);
            if (match) {
              const [, year, month, day] = match;
              return `${day}-${month}-${year}`;
            }
            return dateStr;
          };

          return (
            <TextInput
              {...commonProps}
              type="date"
              value={convertToInputFormat(cell?.value || "")}
              onChange={(event) =>
                updateCell(
                  rowIndex,
                  colIndex,
                  convertToStorageFormat(event.target.value)
                )
              }
            />
          );

        case "url":
          return (
            <TextInput
              {...commonProps}
              type="url"
              onChange={(event) => handleInputChange(event.target.value)}
              onKeyDown={(event) =>
                handleKeyDown(event, "url", column.key)
              }
            />
          );

        case "phone":
          return (
            <TextInput
              {...commonProps}
              type="tel"
              onChange={(event) => handleInputChange(event.target.value)}
              onKeyDown={(event) =>
                handleKeyDown(event, "phone", column.key)
              }
            />
          );

        default:
          return (
            <TextInput
              {...commonProps}
              onChange={(event) => handleInputChange(event.target.value)}
              onKeyDown={(event) => handleKeyDown(event, "text", column.key)}
            />
          );
      }
    },
    [data, columns, highlightErrors, updateCell]
  );


  if (loading) {
    return (
      <Box
        className={className}
        style={{ textAlign: "center", padding: "2rem" }}
      >
        <Loader size="lg" />
        <Text mt="md">Loading spreadsheet...</Text>
      </Box>
    );
  }

  return (
    <Box className={className}>
      {/* Header */}
      {(title || description) && (
        <Box mb="md">
          {title && (
            <Text size="xl" weight={600} mb="xs">
              {title}
            </Text>
          )}
          {description && (
            <Text size="sm" color="dimmed">
              {description}
            </Text>
          )}
        </Box>
      )}

      {/* Validation Summary */}
      {/* {showValidation && validationStats.totalRows > 0 && (
        <Paper
          p="md"
          mb="md"
          radius="md"
          style={{
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Group position="apart" mb="sm">
            <Group spacing="md" align="center">
              <Box
                style={{
                  width: 4,
                  height: 24,
                  background: validationStats.invalidRows === 0
                    ? 'linear-gradient(180deg, #51cf66 0%, #37b24d 100%)'
                    : 'linear-gradient(180deg, #ff6b6b 0%, #fa5252 100%)',
                  borderRadius: 2,
                }}
              />
              <Text
                size="sm"
                weight={600}
                style={{
                  color: '#495057',
                  letterSpacing: '0.3px',
                }}
              >
                Validation Summary
              </Text>
            </Group>

            <Box
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: validationStats.invalidRows === 0
                  ? 'linear-gradient(135deg, #d3f9d8 0%, #b2f2bb 100%)'
                  : 'linear-gradient(135deg, #ffe3e3 0%, #ffc9c9 100%)',
              }}
            >
              {validationStats.invalidRows === 0 &&
                validationStats.totalRows > 0 ? (
                <IconCheck size={18} color="#37b24d" stroke={2.5} />
              ) : (
                <IconAlertCircle size={18} color="#fa5252" stroke={2.5} />
              )}
            </Box>
          </Group>

          <Group spacing="xs" mb={Object.entries(validationStats.errorsByColumn).some(([_, count]) => count > 0) ? "md" : 0}>
            <Box
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                background: '#f1f3f5',
                border: '1px solid #e9ecef',
              }}
            >
              <Text size="xs" weight={500} style={{ color: '#868e96' }}>
                Total
              </Text>
              <Text size="lg" weight={700} style={{ color: '#495057', marginTop: 2 }}>
                {validationStats.totalRows}
              </Text>
            </Box>

            <Box
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                background: 'linear-gradient(135deg, #d3f9d8 0%, #ebfbee 100%)',
                border: '1px solid #b2f2bb',
              }}
            >
              <Text size="xs" weight={500} style={{ color: '#2f9e44' }}>
                Valid
              </Text>
              <Text size="lg" weight={700} style={{ color: '#2b8a3e', marginTop: 2 }}>
                {validationStats.validRows}
              </Text>
            </Box>

            {validationStats.invalidRows > 0 && (
              <Box
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, #ffe3e3 0%, #fff5f5 100%)',
                  border: '1px solid #ffc9c9',
                }}
              >
                <Text size="xs" weight={500} style={{ color: '#c92a2a' }}>
                  Invalid
                </Text>
                <Text size="lg" weight={700} style={{ color: '#e03131', marginTop: 2 }}>
                  {validationStats.invalidRows}
                </Text>
              </Box>
            )}

            {validationStats.requiredFieldsEmpty > 0 && (
              <Box
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, #fff3bf 0%, #fff9db 100%)',
                  border: '1px solid #ffe066',
                }}
              >
                <Text size="xs" weight={500} style={{ color: '#e67700' }}>
                  Missing
                </Text>
                <Text size="lg" weight={700} style={{ color: '#f76707', marginTop: 2 }}>
                  {validationStats.requiredFieldsEmpty}
                </Text>
              </Box>
            )}
          </Group>

          {/* Column-wise error summary */}
      {/* {Object.entries(validationStats.errorsByColumn).some(
            ([_, count]) => count > 0
          ) && (
              <Box
                mt="md"
                pt="md"
                style={{
                  borderTop: '1px solid #e9ecef',
                }}
              >
                <Text
                  size="xs"
                  weight={600}
                  mb="xs"
                  style={{
                    color: '#868e96',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Errors by Column
                </Text>
                <Group spacing="xs">
                  {Object.entries(validationStats.errorsByColumn)
                    .filter(([_, count]) => count > 0)
                    .map(([columnKey, count]) => (
                      <Badge
                        key={columnKey}
                        size="sm"
                        radius="md"
                        variant="light"
                        style={{
                          background: '#fff5f5',
                          color: '#e03131',
                          border: '1px solid #ffc9c9',
                          padding: '6px 10px',
                          fontWeight: 500,
                        }}
                      >
                        {columns.find((col) => col.key === columnKey)?.label ||
                          columnKey}
                        <Text component="span" weight={700} ml={4}>
                          {count}
                        </Text>
                      </Badge>
                    ))}
                </Group>
              </Box>
            )}
        </Paper>
      )} */}

      {/* Toolbar */}
      <Paper p="xl" my="md" withBorder>
        <Group position="apart">
          <Group spacing="xs">
            {/* Search */}
            {enableSearch && (
              <TextInput
                placeholder="Search..."
                icon={<IconSearch size={19} />}
                size="sm"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                style={{ width: 350 }}
              />
            )}

            {/* Add rows */}
            {allowAddRows && (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    variant="light"
                    leftIcon={<IconPlus size={16} />}
                    size="sm"
                    disabled={data.length >= maxRows}
                  >
                    Add Rows
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={addRow}>Add 1 Row</Menu.Item>
                  <Menu.Item onClick={() => addMultipleRows(5)}>
                    Add 5 Rows
                  </Menu.Item>
                  <Menu.Item onClick={() => addMultipleRows(10)}>
                    Add 10 Rows
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}

            {/* Bulk operations */}
            {allowBulkOperations && selectedRows.size > 0 && (
              <Button
                variant="light"
                color="red"
                leftIcon={<IconTrash size={16} />}
                size="sm"
                onClick={deleteSelectedRows}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            )}
          </Group>

          <Group spacing="xs">
            {/* Export */}
            {enableExport && (
              <Button
                variant="outline"
                leftIcon={<IconDownload size={16} />}
                size="sm"
                onClick={() => exportToCSV(data, columns)}
              >
                Export CSV
              </Button>
            )}


          </Group>
        </Group>
      </Paper>

      {/* Spreadsheet Table */}
      <Paper p="xl" my="md" withBorder>
        <ScrollArea style={{ height: virtualScrolling ? 400 : "auto" }}>
          <BenchTable

          >
            <thead>
              <tr>
                {/* Row selection checkbox */}
                {allowBulkOperations && (
                  <th style={{ width: 40 }}>
                    <Checkbox
                      size="xs"
                      checked={
                        selectedRows.size === processedData.length &&
                        processedData.length > 0
                      }
                      indeterminate={
                        selectedRows.size > 0 &&
                        selectedRows.size < processedData.length
                      }
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedRows(
                            new Set(processedData.map((_, index) => index))
                          );
                        } else {
                          setSelectedRows(new Set());
                        }
                      }}
                    />
                  </th>
                )}

                <th style={{ width: 50 }}>#</th>

                {columns
                  .filter((col) => !col.hidden)
                  .map((column, index) => (
                    <th
                      key={index}
                      style={{
                        textWrap: "nowrap",
                        minWidth: column.width || 150,
                        cursor: column.sortable ? "pointer" : "default",
                      }}
                      onClick={() => handleSort(index)}
                    >
                      <Group spacing={4}>
                        <Text size="xs" weight={500}>
                          {column.label || column.key}
                        </Text>
                        {column.required && (
                          <Text color="red" size="xs">
                            *
                          </Text>
                        )}
                        {column.sortable &&
                          sortConfig?.columnIndex === index &&
                          (sortConfig.direction === "asc" ? (
                            <IconSortAscending size={12} />
                          ) : (
                            <IconSortDescending size={12} />
                          ))}

                      </Group>
                    </th>
                  ))}

                {allowDeleteRows && <th style={{ width: 50 }}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {processedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {/* Row selection */}
                  {allowBulkOperations && (
                    <td>
                      <Checkbox
                        size="xs"
                        checked={selectedRows.has(rowIndex)}
                        onChange={(event) => {
                          const newSet = new Set(selectedRows);
                          if (event.target.checked) {
                            newSet.add(rowIndex);
                          } else {
                            newSet.delete(rowIndex);
                          }
                          setSelectedRows(newSet);
                        }}
                      />
                    </td>
                  )}

                  <td>
                    <Text size="xs" color="dimmed">
                      {rowIndex + 1}
                    </Text>
                  </td>

                  {columns.map(
                    (column, colIndex) =>
                      !column.hidden && (
                        <td key={colIndex}>
                          {renderCell(
                            row[colIndex] || { value: "" },
                            rowIndex,
                            colIndex
                          )}
                        </td>
                      )
                  )}

                  {allowDeleteRows && (
                    <td>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        onClick={() => deleteRow(rowIndex)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </BenchTable>
        </ScrollArea>
      </Paper>

      {/* Footer Information */}
      <Paper p="xs" mt="md" style={{ backgroundColor: "#f8f9fa" }}>
        <Group position="apart">
          <Group spacing="lg">
            {highlightErrors && (
              <>
                <Group spacing={4}>
                  <Box
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: "#ffe6e6",
                      border: "1px solid #ff6b6b",
                      borderRadius: 2,
                    }}
                  />
                  <Text size="xs" color="dimmed">
                    Validation Error
                  </Text>
                </Group>
                <Group spacing={4}>
                  <Text size="xs" color="red">
                    *
                  </Text>
                  <Text size="xs" color="dimmed">
                    Required Field
                  </Text>
                </Group>
              </>
            )}
          </Group>

          <Group spacing="xs">
            <Text size="xs" color="dimmed">
              Showing {processedData.length} of {data.length} rows
            </Text>
            {autoSave && (
              <Badge size="xs" color="green" variant="outline">
                Auto-save enabled
              </Badge>
            )}
          </Group>
        </Group>
      </Paper>
    </Box>
  );
};

export default EnhancedSpreadsheet;
